import { createSelector } from '@reduxjs/toolkit';
import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { shallowEqual } from '../utils/shallowEqual.js';
import { useShallowStableValue } from '../utils/useShallowStableValue.js';
import { skipSelector } from '../core/buildSelectors.js';

const defaultQueryStateSelector = (currentState, lastResult) => {
  var _ref;

  // data is the last known good request result we have tracked - or if none has been tracked yet the last good result for the current args
  const data = (_ref = currentState.isSuccess ? currentState.data : lastResult == null ? void 0 : lastResult.data) != null ? _ref : currentState.data; // isFetching = true any time a request is in flight

  const isFetching = currentState.isLoading; // isLoading = true only when loading while no data is present yet (initial load with no data in the cache)

  const isLoading = !data && isFetching; // isSuccess = true when data is present

  const isSuccess = currentState.isSuccess || isFetching && !!data;
  return { ...currentState,
    data,
    isFetching,
    isLoading,
    isSuccess
  };
};

function buildHooks({
  api,
  moduleOptions: {
    batch,
    useDispatch,
    useSelector
  }
}) {
  return {
    buildQueryHooks,
    buildMutationHook,
    usePrefetch
  };

  function usePrefetch(endpointName, defaultOptions) {
    const dispatch = useDispatch();
    const stableDefaultOptions = useShallowStableValue(defaultOptions);
    return useCallback((arg, options) => dispatch(api.util.prefetchThunk(endpointName, arg, { ...stableDefaultOptions,
      ...options
    })), [endpointName, dispatch, stableDefaultOptions]);
  }

  function buildQueryHooks(name) {
    const useQuerySubscription = (arg, {
      refetchOnReconnect,
      refetchOnFocus,
      refetchOnMountOrArgChange,
      skip = false,
      pollingInterval = 0
    } = {}) => {
      const {
        initiate
      } = api.endpoints[name];
      const dispatch = useDispatch();
      const stableArg = useShallowStableValue(arg);
      const promiseRef = useRef();
      useEffect(() => {
        if (skip) {
          return;
        }

        const lastPromise = promiseRef.current;

        if (lastPromise && lastPromise.arg === stableArg) {
          // arg did not change, but options did probably, update them
          lastPromise.updateSubscriptionOptions({
            pollingInterval,
            refetchOnReconnect,
            refetchOnFocus
          });
        } else {
          if (lastPromise) lastPromise.unsubscribe();
          const promise = dispatch(initiate(stableArg, {
            subscriptionOptions: {
              pollingInterval,
              refetchOnReconnect,
              refetchOnFocus
            },
            forceRefetch: refetchOnMountOrArgChange
          }));
          promiseRef.current = promise;
        }
      }, [stableArg, dispatch, skip, pollingInterval, refetchOnMountOrArgChange, refetchOnFocus, refetchOnReconnect, initiate]);
      useEffect(() => {
        return () => {
          var _promiseRef$current;

          return void ((_promiseRef$current = promiseRef.current) == null ? void 0 : _promiseRef$current.unsubscribe());
        };
      }, []);
      return useMemo(() => ({
        refetch: () => {
          var _promiseRef$current2;

          return void ((_promiseRef$current2 = promiseRef.current) == null ? void 0 : _promiseRef$current2.refetch());
        }
      }), []);
    };

    const useQueryState = (arg, {
      skip = false,
      selectFromResult = defaultQueryStateSelector
    } = {}) => {
      const {
        select
      } = api.endpoints[name];
      const stableArg = useShallowStableValue(arg);
      const lastValue = useRef();
      const querySelector = useMemo(() => createSelector([select(skip ? skipSelector : stableArg), (_, lastResult) => lastResult], (subState, lastResult) => selectFromResult(subState, lastResult, defaultQueryStateSelector)), [select, skip, stableArg, selectFromResult]);
      const currentState = useSelector(state => querySelector(state, lastValue.current), shallowEqual);
      useEffect(() => {
        lastValue.current = currentState;
      }, [currentState]);
      return currentState;
    };

    return {
      useQueryState,
      useQuerySubscription,

      useQuery(arg, options) {
        const querySubscriptionResults = useQuerySubscription(arg, options);
        const queryStateResults = useQueryState(arg, options);
        return useMemo(() => ({ ...queryStateResults,
          ...querySubscriptionResults
        }), [queryStateResults, querySubscriptionResults]);
      }

    };
  }

  function buildMutationHook(name) {
    return () => {
      const {
        select,
        initiate
      } = api.endpoints[name];
      const dispatch = useDispatch();
      const [requestId, setRequestId] = useState();
      const promiseRef = useRef();
      useEffect(() => () => {
        var _promiseRef$current3;

        return void ((_promiseRef$current3 = promiseRef.current) == null ? void 0 : _promiseRef$current3.unsubscribe());
      }, []);
      const triggerMutation = useCallback(function (args) {
        let promise;
        batch(() => {
          if (promiseRef.current) promiseRef.current.unsubscribe();
          promise = dispatch(initiate(args));
          promiseRef.current = promise;
          setRequestId(promise.requestId);
        });
        return promise;
      }, [dispatch, initiate]);
      const mutationSelector = useMemo(() => select(requestId || skipSelector), [requestId, select]);
      const currentState = useSelector(mutationSelector);
      return useMemo(() => [triggerMutation, currentState], [triggerMutation, currentState]);
    };
  }
}

export { buildHooks };
//# sourceMappingURL=buildHooks.js.map
